/* eslint-disable fp/no-mutating-methods */
import { LevelActionType } from '~/bootcamp/types/bootcamp.types'
import { CurriculumCountryCodes, CurriculumGradeLevelType } from '~/curriculum/constants/curriculum.const'
import {
  BootcampLevel,
  CurriculumCompetencyDataType,
  CurriculumLevelDataType,
  CurriculumTaskActivities,
  FilteredOutputSubject,
  GradeCompetency,
  QuartarDataType,
} from '~/curriculum/types/curriculum.types'
import { TopicId } from '~/dashboard/utils/learningPath.utils'

const compIgnoreList = [
  CurriculumCountryCodes.USA,
  CurriculumCountryCodes.UKRAINIAN,
  CurriculumCountryCodes.DEFAULT,
  CurriculumCountryCodes.NORWAY,
]

const shouldIgnoreCompetency = (countryCode: string): boolean =>
  compIgnoreList.includes(countryCode as CurriculumCountryCodes)

export const getGradeIdForSanityDataFetch = (curriculumCountryCode: string, grade: number | string): string => {
  const countryCode =
    curriculumCountryCode === CurriculumCountryCodes.DEFAULT ||
    curriculumCountryCode === CurriculumCountryCodes.UKRAINIAN
      ? CurriculumCountryCodes.USA
      : curriculumCountryCode
  return `grade-${countryCode}-${grade}`
}

const addSubjectOrder = (subjectList: any, subjectOrderArray: any): any => {
  if (!subjectOrderArray && !subjectOrderArray?.length) {
    return subjectList
  }

  const subjectsWithOrder = subjectList.map((subject: any) => {
    const index = subjectOrderArray.findIndex((el: any) => el.subject === subject.subjectLabel)

    if (index === -1) {
      return {
        ...subject,
      }
    }

    return {
      ...subject,
      subjectOrder: subjectOrderArray[index].order,
      subjectOrderLabel: subjectOrderArray[index].subjectLabel ?? '',
      icon: subjectOrderArray[index]?.icon ?? '',
    }
  })

  return subjectsWithOrder.sort((a: any, b: any) => a.subjectOrder - b.subjectOrder)
}

function convertBootcampPathToIds(path: string, activities: CurriculumTaskActivities[] | undefined): string[] {
  const [, , , key, moduleNumber, level, type] = path.split('/')
  const baseId = `${key}#${moduleNumber}#${level}`
  let result = [`${baseId}#${type}`]

  if (activities) {
    activities.forEach((activity) => {
      activity.difficultyLevels.forEach((difficultyLevel, index) => {
        const nid = `${baseId}#activity#${activity.activitySet}#${index + 1}`
        const activityIds = Array.from({ length: difficultyLevel.rewards }, (_, ind) => `${nid}#${ind + 1}`)
        result = [...result, ...activityIds]
      })
    })
  }

  return result
}

function convertMathVaultPathToIds(globalId: string, path: string): string[] {
  if (!path) {
    return []
  }
  const parts = path.split('/').filter(Boolean) // Remove empty strings caused by leading/trailing slashes
  const secondLastElement = parts[parts.length - 2]
  return [`${globalId}#${secondLastElement}`]
}
const filterActivityLevels = (
  bootcampLevels: BootcampLevel[],
  curriculumGradeDetails: { competencyGoals: GradeCompetency[] },
  mathVaultLevels: any,
  countryCode: string,
  divisionLevel?: number,
): Array<{
  subjectLabel: string
  topicId: TopicId
  icon?: string
  subjectOrder: number
  competencyGoals: CurriculumCompetencyDataType[]
}> => {
  const bootcampSubjectDetails: FilteredOutputSubject = {}

  // Helper function to add levels to the subject structure
  const addLevel = (
    subjectLabel: string,
    topicId: TopicId,
    competencyGoalCode: string,
    description: string,
    subdivisionLevel: number,
    levelData: CurriculumLevelDataType,
  ): void => {
    const subject = bootcampSubjectDetails[subjectLabel] || {
      subjectLabel,
      topicId,
      competencyGoals: {},
    }

    const competencyGoal = subject.competencyGoals[competencyGoalCode] || {
      competencyGoalCode,
      description,
      subdivisionOrder: countryCode === CurriculumCountryCodes.PHILIPPINES ? subdivisionLevel : 0,
      levels: [],
    }

    bootcampSubjectDetails[subjectLabel] = {
      ...subject,
      competencyGoals: {
        ...subject.competencyGoals,
        [competencyGoalCode]: {
          ...competencyGoal,
          levels: [...competencyGoal.levels, levelData],
        },
      },
    }
  }

  // Process bootcamp levels
  bootcampLevels.forEach((bootcampLevel) => {
    const { activityLevels, hasWhyLevel, whyLevel } = bootcampLevel

    if (hasWhyLevel && whyLevel && whyLevel.path) {
      const { title, path, competency, hasEncyclopedia } = whyLevel
      const currentCompetency = divisionLevel
        ? competency.find((c) => c.subdivisionLevel === divisionLevel)
        : competency[0]
      if (!currentCompetency || (divisionLevel && currentCompetency.subdivisionLevel !== divisionLevel)) return

      const {
        topicId,
        level: competencyGoalCode,
        levelOrder,
        subject: subjectLabel,
        description,
        subdivisionLevel,
      } = currentCompetency

      addLevel(subjectLabel, topicId, competencyGoalCode, description, subdivisionLevel, {
        title,
        levelOrder,
        path,
        type: CurriculumGradeLevelType.BOOTCAMP,
        iconType: hasEncyclopedia ? LevelActionType.Encyclopedia : LevelActionType.Bootcamp,
        isLastVisited: false,
        isCompleted: false,
        levelIds: convertBootcampPathToIds(path, undefined),
      })
    }

    activityLevels.forEach((activityLevel) => {
      const { title, path, competency, hasEncyclopedia, activities } = activityLevel
      const currentCompetency = divisionLevel
        ? competency.find((c) => c.subdivisionLevel === divisionLevel)
        : competency[0]
      if (!currentCompetency || (divisionLevel && currentCompetency.subdivisionLevel !== divisionLevel)) return

      const {
        topicId,
        level: competencyGoalCode,
        levelOrder,
        subject: subjectLabel,
        description,
        subdivisionLevel,
      } = currentCompetency

      addLevel(subjectLabel, topicId, competencyGoalCode, description, subdivisionLevel, {
        title,
        levelOrder,
        path: path ?? undefined,
        type: CurriculumGradeLevelType.BOOTCAMP,
        iconType: hasEncyclopedia ? LevelActionType.Encyclopedia : LevelActionType.Bootcamp,
        isLastVisited: false,
        isCompleted: false,
        levelIds: convertBootcampPathToIds(path ?? '', activities),
      })
    })
  })

  // Process encyclopedia and AI levels
  curriculumGradeDetails.competencyGoals.forEach((gradeCompetency) => {
    if (!gradeCompetency || (divisionLevel && gradeCompetency.subdivisionLevel !== divisionLevel)) return
    const {
      subject: subjectLabel,
      topicId,
      description,
      aiPrompts = [],
      encyclopediaUrls = [],
      level: competencyGoalCode,
      subdivisionLevel,
    } = gradeCompetency

    const filteredAiPrompts = countryCode === CurriculumCountryCodes.USA ? [] : aiPrompts
    const levels: CurriculumLevelDataType[] = [
      ...filteredAiPrompts.map(({ id, title, prompt, levelOrder }) => ({
        title,
        type: CurriculumGradeLevelType.AI,
        prompt,
        path: undefined,
        curriculumLevelId: id,
        iconType: LevelActionType.AI,
        isLastVisited: false,
        isCompleted: false,
        levelOrder,
        levelIds: [id],
      })),
      ...encyclopediaUrls.map(({ title, url }) => ({
        title,
        type: CurriculumGradeLevelType.ENCYCLOPEDIA,
        prompt: undefined,
        path: url,
        iconType: LevelActionType.Encyclopedia,
        isLastVisited: false,
        isCompleted: false,
        levelIds: [url],
      })),
    ]

    levels.forEach((level) => {
      addLevel(subjectLabel, topicId, competencyGoalCode, description, subdivisionLevel, level)
    })
  })

  // Process Math Vault levels
  if (Array.isArray(mathVaultLevels) && mathVaultLevels.length > 0) {
    mathVaultLevels.forEach((mathVaultLevel: any) => {
      const { type, details, competencies } = mathVaultLevel || {}
      competencies.forEach((competency: any) => {
        const {
          topicId,
          subject,
          competencyGoalCode,
          competencyDescription,
          globalId,
          title,
          path,
          levelOrder,
          subdivisionLevel,
        } = competency
        if (!competency || (divisionLevel && competency.subdivisionLevel !== divisionLevel)) return

        const level = {
          title,
          type,
          prompt: undefined,
          path,
          globalId,
          curriculumLevelId: globalId,
          iconType: type,
          isLastVisited: false,
          isCompleted: false,
          levelIds: convertMathVaultPathToIds(globalId, path),
          levelOrder,
          details,
        }
        addLevel(subject, topicId, competencyGoalCode, competencyDescription, subdivisionLevel, level)
      })
    })
  }

  const hideCompetencyLabel = shouldIgnoreCompetency(countryCode)
  // Convert bootcampSubjectDetails to an array format
  return Object.values(bootcampSubjectDetails).map(({ subjectLabel, topicId, competencyGoals }) => ({
    subjectLabel,
    topicId,
    competencyGoals: Object.values(competencyGoals).map((goal) => {
      if (goal.levels) {
        const levelsArray = Array.from(goal.levels.values())
        levelsArray.sort((a: any, b: any) => a.levelOrder - b.levelOrder)
        goal.levels = levelsArray
      }
      return {
        ...goal,
        competencyGoalCode: hideCompetencyLabel ? undefined : goal.competencyGoalCode,
      }
    }),
    subjectOrder: 0,
  }))
}

export const formatCurriculumContent = (content: any, countryCode: string): any => {
  const { gradeDetails, bootcampLevels, mathVaultLevels } = content || {}

  let gradeSubdivisions: QuartarDataType[] = []

  if (gradeDetails?.hasSubdivision && countryCode !== CurriculumCountryCodes.DEFAULT) {
    gradeDetails.subdivisions.forEach((division: any) => {
      const subdivisionLabel = division.label
      const subdivisionOrder = division.level
      const subdivisionIcon = division.icon
      const subjectOrder = gradeDetails.subjectOrder.filter((so: any) => so.subdivisionLevel === subdivisionOrder)
      let subjects = filterActivityLevels(bootcampLevels, gradeDetails, mathVaultLevels, countryCode, division.level)
      subjects = addSubjectOrder(subjects, subjectOrder)

      gradeSubdivisions.push({
        subdivisionLabel,
        subdivisionOrder,
        subdivisionIcon,
        subjects,
      })
    })
  } else {
    let subjects = filterActivityLevels(bootcampLevels, gradeDetails, mathVaultLevels, countryCode)
    subjects = addSubjectOrder(subjects, gradeDetails.subjectOrder)

    const subdivisionOrder = 0
    gradeSubdivisions.push({
      subdivisionOrder,
      subjects,
    })
  }

  gradeSubdivisions = gradeSubdivisions.sort((a, b) => a.subdivisionOrder - b.subdivisionOrder)

  // destructure & set defaults for necessary gradeDetails values
  const {
    gradeLabel = null,
    gradeLevel = null,
    title = null,
    isJuniorMathGrade = null,
    gradeCode = null,
  } = gradeDetails || {}

  let hasSubdivision = gradeDetails?.hasSubdivision || false

  if (countryCode === CurriculumCountryCodes.DEFAULT) {
    hasSubdivision = false
  }

  let newGradeLabel = gradeLabel
  if (countryCode === CurriculumCountryCodes.USA && hasSubdivision && gradeDetails?.subdivisions.length) {
    newGradeLabel = gradeDetails?.subdivisions.find((el: any) => Number.parseInt(el.level) === 1)?.label || gradeLabel
  }

  return {
    gradeLabel: newGradeLabel,
    gradeLevel,
    gradeCode,
    title,
    isJuniorMathGrade,
    hasSubdivision,
    gradeSubdivisions,
  }
}

export const formatDateForCurriculumCertificate = (date: string): string => {
  return new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export const randomNumbersToGenerateXpForPrerequisites = [
  193, 163, 175, 160, 152, 199, 161, 178, 179, 167, 158, 163, 186, 199, 186, 191, 177, 164, 172, 158, 200, 155, 189,
  160, 187,
]

/**
 * This function convert country code to curriculum relevant country code and grade id accordingly
 * Example:
 * countryCode: ua -> curriculumCountryCode: us
 * countryCode: default -> curriculumCountryCode: us
 * gradeId: grade-ua-1 -> grade-us-1
 *
 * @param countryCode - The country code
 * @param gradeId - The grade id of curriculum (optional)
 * @returns {
 *  curriculumCountryCode: string
 *  gradeId: string
 * }
 */
export const getCurriculumRelevantCountryCodeAndGradeId = (
  countryCode: string,
  gradeId?: string,
): {
  curriculumCountryCode: string
  gradeId?: string
} => {
  const curriculumCountryCode =
    countryCode === CurriculumCountryCodes.UKRAINIAN || countryCode === CurriculumCountryCodes.DEFAULT
      ? CurriculumCountryCodes.USA
      : countryCode

  return {
    curriculumCountryCode,
    gradeId: gradeId ? `grade-${curriculumCountryCode}-${gradeId.split('-')[2]}` : undefined,
  }
}

export const slugify = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}
